<template>
    <div
        class="
            w-0 h-0 overflow-hidden absolute flex flex-col items-center justify-center py-1 bg-[#E1FBC9]
            focus-within:relative focus-within:w-full focus-within:h-auto focus-within:overflow-visible
            md:flex-row md:items-start md:gap-4
        "
        role="navigation"
        :aria-label="$t('layout.skipLinks.label')"
    >
        <a
            v-for="link in skipLinks"
            :key="link.id"
            :href="`#${link.id}`"
            class="btn btn-ghost text-sm border-transparent hover:bg-transparent hover:border-transparent underline"
            @click.prevent="setFocusTo(link.id)"
        >
            {{ link.text }}
        </a>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

interface SkipLink {
    id: string;
    text: string;
}

const props = defineProps<{
    customLinks?: SkipLink[]
}>()

const { t } = useI18n()

// Default skip links to important page sections
const defaultLinks: SkipLink[] = [
    { id: 'header', text: t('layout.skipLinks.header') },
    { id: 'main', text: t('layout.skipLinks.main') },
    { id: 'footer', text: t('layout.skipLinks.footer') }
]

// Combine default and custom links
const skipLinks = computed<SkipLink[]>(() => {
    return [...defaultLinks, ...(props.customLinks || [])]
})

// Function to set focus to the target element
const setFocusTo = (id: string): void => {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
        const element = document.getElementById(id) ||
            document.querySelector(`#${id}, ${id}, [data-skip-id="${id}"]`) as HTMLElement

        if (element) {
            // If element doesn't have tabindex, add it temporarily
            const hadTabIndex = element.hasAttribute('tabindex')
            if (!hadTabIndex) {
                element.setAttribute('tabindex', '-1')
            }

            // Set focus to the element
            element.focus()

            // If we added a tabindex and the element is not naturally focusable,
            // remove it after focus (but leave it if it needs to be in tab order)
            if (!hadTabIndex && !isNaturallyFocusable(element)) {
                // Keep tabindex for a short while to maintain focus
                setTimeout(() => {
                    element.removeAttribute('tabindex')
                }, 1000)
            }
        } else {
            console.warn(`Skip target with id '${id}' not found in the document.`)
        }
    }, 100)
}

// Helper function to check if an element is naturally focusable
const isNaturallyFocusable = (element: HTMLElement): boolean => {
    const focusableElements = ['a', 'button', 'input', 'select', 'textarea']
    return focusableElements.includes(element.tagName.toLowerCase()) ||
        element.hasAttribute('tabindex')
}
</script>
